import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import AboutHero from './AboutHero';
import Swal from 'sweetalert2';
import './Login.css'; // Reuse form styles from Login.css

const ct = {
  password: {
    en: "Password",
    nl: "Wachtwoord",
    es: "Contraseña",
    fr: "Mot de passe",
    de: "Passwort",
    it: "Password",
    pt: "Senha",
    el: "Κωδικός πρόσβασης"
  },
  signUp: {
    en: "Sign Up",
    nl: "Aanmelden",
    es: "Registrarse",
    fr: "S'inscrire",
    de: "Registrieren",
    it: "Registrati",
    pt: "Cadastrar",
    el: "Εγγραφή"
  }
};

const Register = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;

  const getLocText = (key) => {
    return ct[key]?.[selectedLang.code] || ct[key]?.en || "";
  };

  const [userType, setUserType] = useState('normal'); // 'normal' or 'b2b'

  // Normal Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  // B2B Form state
  const [b2bFullName, setB2bFullName] = useState('');
  const [b2bEmail, setB2bEmail] = useState('');
  const [b2bPhone, setB2bPhone] = useState('');
  const [b2bBusinessName, setB2bBusinessName] = useState('');
  const [b2bTaxNumber, setB2bTaxNumber] = useState('');
  const [b2bPassword, setB2bPassword] = useState('');
  const [b2bAgreeTerms, setB2bAgreeTerms] = useState(false);

  useEffect(() => {
    document.title = `${ct.signUp[selectedLang.code || 'en'] || 'Sign Up'} | Smart Telecom Store`;
    window.scrollTo(0, 0);
  }, [selectedLang.code]);

  const handleNavigate = (path, e) => {
    e.preventDefault();
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreeTerms) {
      Swal.fire({
        title: t.validationError || 'Validation Error',
        text: t.mustAgreeTerms || 'You must agree to the Terms & Conditions to proceed.',
        icon: 'warning',
        confirmButtonColor: '#be1e2d'
      });
      return;
    }

    // Show processing state
    Swal.fire({
      title: t.creatingAccount || 'Creating Account...',
      text: t.pleaseWaitConnect || 'Please wait while we connect to the database.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    fetch('/api/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fullName, email, password }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          Swal.fire({
            title: t.registrationFailed || 'Registration Failed',
            text: data.error,
            icon: 'error',
            confirmButtonColor: '#be1e2d'
          });
        } else {
          Swal.fire({
            title: t.success || 'Success!',
            text: t.accountCreatedSuccess || 'Your account has been created successfully. Redirecting to login...',
            icon: 'success',
            timer: 2500,
            showConfirmButton: false
          }).then(() => {
            // Navigate to login
            window.history.pushState({}, '', '/login');
            window.dispatchEvent(new PopStateEvent('popstate'));
          });
        }
      })
      .catch((err) => {
        console.error('Registration error:', err);
        Swal.fire({
          title: t.connectionErrorTitle || 'Connection Error',
          text: t.authConnectionErrorText || t.connectionErrorText || 'Could not connect to authentication server.',
          icon: 'error',
          confirmButtonColor: '#be1e2d'
        });
      });
  };

  const handleB2bSubmit = (e) => {
    e.preventDefault();
    if (!b2bAgreeTerms) {
      Swal.fire({
        title: t.validationError || 'Validation Error',
        text: 'You must agree to the Terms & Conditions to proceed.',
        icon: 'warning',
        confirmButtonColor: '#be1e2d'
      });
      return;
    }

    Swal.fire({
      title: 'Submitting B2B Application...',
      text: 'Please wait while we register your business details.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    fetch('/api/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        fullName: b2bFullName, 
        email: b2bEmail, 
        password: b2bPassword,
        phone: b2bPhone,
        businessName: b2bBusinessName,
        taxNumber: b2bTaxNumber,
        isB2B: true 
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          Swal.fire({
            title: 'Registration Failed',
            text: data.error,
            icon: 'error',
            confirmButtonColor: '#be1e2d'
          });
        } else {
          Swal.fire({
            title: 'B2B Account Created!',
            text: 'Your B2B account application has been submitted successfully. Redirecting to login...',
            icon: 'success',
            timer: 3000,
            showConfirmButton: false
          }).then(() => {
            window.history.pushState({}, '', '/login');
            window.dispatchEvent(new PopStateEvent('popstate'));
          });
        }
      })
      .catch((err) => {
        console.error('B2B Registration error:', err);
        Swal.fire({
          title: 'Connection Error',
          text: 'Could not connect to authentication server.',
          icon: 'error',
          confirmButtonColor: '#be1e2d'
        });
      });
  };

  return (
    <>
      <AboutHero />
      <div className="auth-page-wrapper">
        <div className="auth-container">
          {/* Left Column: Image */}
          <div className="auth-image-col">
            <img
              src="https://images.unsplash.com/photo-1527689368864-3a821dbccc34?w=800&auto=format&fit=crop&q=80"
              alt="Sign Up to Smart Telecom"
              className="auth-image"
            />
          </div>

          {/* Right Column: Form */}
          <div className="auth-form-col">
            <div className="auth-form-box">
              <h1 className="auth-heading">{t.createAccount || 'Create Account'}</h1>
              <p className="auth-subheading">{t.authSubheadingRegister || 'Sign up in seconds to start shopping'}</p>

              {/* Tabs */}
              <div className={`auth-tabs ${userType === 'b2b' ? 'b2b-active' : ''}`}>
                <button
                  type="button"
                  className={`auth-tab-btn ${userType === 'normal' ? 'active' : ''}`}
                  onClick={() => setUserType('normal')}
                >
                  Normal User
                </button>
                <button
                  type="button"
                  className={`auth-tab-btn ${userType === 'b2b' ? 'active' : ''}`}
                  onClick={() => setUserType('b2b')}
                >
                  B2B User
                </button>
              </div>

              {/* Sliding Form Wrapper */}
              <div className="auth-form-slider-container">
                <div className={`auth-form-slider ${userType === 'b2b' ? 'slide-b2b' : 'slide-normal'}`}>
                  {/* Normal User Form */}
                  <div className="auth-form-slide">
                    <form onSubmit={handleSubmit} className="auth-form">
                      <div className="auth-input-group">
                        <label htmlFor="fullName">{t.fullName || 'Full Name'}</label>
                        <input
                          type="text"
                          id="fullName"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder={t.fullNamePlaceholder || 'Enter your full name'}
                          required={userType === 'normal'}
                        />
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="email">{t.emailAddress || 'Email Address'}</label>
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t.emailPlaceholder || 'Enter your email'}
                          required={userType === 'normal'}
                        />
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="password">{getLocText('password')}</label>
                        <input
                          type="password"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t.createPasswordPlaceholder || 'Create a password'}
                          required={userType === 'normal'}
                        />
                      </div>

                      <div className="auth-options-row">
                        <label className="remember-me-checkbox">
                          <input
                            type="checkbox"
                            checked={agreeTerms}
                            onChange={(e) => setAgreeTerms(e.target.checked)}
                            required={userType === 'normal'}
                          />
                          <span className="checkbox-custom-box"></span>
                          {t.agreeTerms || 'I agree to the Terms & Conditions'}
                        </label>
                      </div>

                      <button type="submit" className="auth-submit-btn-primary">
                        {getLocText('signUp')}
                      </button>
                    </form>
                  </div>

                  {/* B2B User Form */}
                  <div className="auth-form-slide">
                    <form onSubmit={handleB2bSubmit} className="auth-form">
                      <div className="auth-input-group">
                        <label htmlFor="b2bFullName">Full Name</label>
                        <input
                          type="text"
                          id="b2bFullName"
                          value={b2bFullName}
                          onChange={(e) => setB2bFullName(e.target.value)}
                          placeholder="Enter your full name"
                          required={userType === 'b2b'}
                        />
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="b2bEmail">Email address</label>
                        <input
                          type="email"
                          id="b2bEmail"
                          value={b2bEmail}
                          onChange={(e) => setB2bEmail(e.target.value)}
                          placeholder="Enter your email"
                          required={userType === 'b2b'}
                        />
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="b2bPhone">Phone Number</label>
                        <input
                          type="tel"
                          id="b2bPhone"
                          value={b2bPhone}
                          onChange={(e) => setB2bPhone(e.target.value)}
                          placeholder="Enter your phone number"
                          required={userType === 'b2b'}
                        />
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="b2bBusinessName">Business Name</label>
                        <input
                          type="text"
                          id="b2bBusinessName"
                          value={b2bBusinessName}
                          onChange={(e) => setB2bBusinessName(e.target.value)}
                          placeholder="Enter your business name"
                          required={userType === 'b2b'}
                        />
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="b2bTaxNumber">Tax number</label>
                        <input
                          type="text"
                          id="b2bTaxNumber"
                          value={b2bTaxNumber}
                          onChange={(e) => setB2bTaxNumber(e.target.value)}
                          placeholder="Enter your tax number"
                          required={userType === 'b2b'}
                        />
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="b2bPassword">Password</label>
                        <input
                          type="password"
                          id="b2bPassword"
                          value={b2bPassword}
                          onChange={(e) => setB2bPassword(e.target.value)}
                          placeholder="Create a password"
                          required={userType === 'b2b'}
                        />
                      </div>

                      <div className="auth-options-row">
                        <label className="remember-me-checkbox">
                          <input
                            type="checkbox"
                            checked={b2bAgreeTerms}
                            onChange={(e) => setB2bAgreeTerms(e.target.checked)}
                            required={userType === 'b2b'}
                          />
                          <span className="checkbox-custom-box"></span>
                          I agree to B2B Terms & Conditions
                        </label>
                      </div>

                      <button type="submit" className="auth-submit-btn-primary">
                        Register Business
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              <p className="auth-switch-text">
                {t.alreadyHaveAccount || 'Already have an account?'}{' '}
                <a
                  href="/login"
                  onClick={(e) => handleNavigate('/login', e)}
                  className="auth-switch-link"
                >
                  {t.signIn || 'Sign In'}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;

